import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { LISTING_STATE_DRAFT } from '../../util/types';
import { ListingLink } from '..';
import { EditListingTouringLinksForm } from '../../forms';
import { ensureOwnListing } from '../../util/data';
import { types as sdkTypes } from '../../util/sdkLoader';
import config from '../../config';

import css from './EditListingTouringLinksPanel.module.css';

const { Money, LatLng } = sdkTypes;

const EditListingTouringLinksPanel = props => {
  const {
    className,
    rootClassName,
    listing,
    disabled,
    ready,
    onSubmit,
    onChange,
    submitButtonText,
    panelUpdated,
    updateInProgress,
    errors,
    onGetPrevPathParams,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureOwnListing(listing);
  const { title, price, publicData } = currentListing.attributes;

  const isPublished = currentListing.id && currentListing.attributes.state !== LISTING_STATE_DRAFT;
  const panelTitle = isPublished ? (
    <FormattedMessage
      id="EditListingTouringLinksPanel.title"
      values={{
        listingTitle: (
          <ListingLink className={css.listingLink} listing={listing}>
            {title}
          </ListingLink>
        ),
      }}
    />
  ) : (
    <FormattedMessage id="EditListingTouringLinksPanel.createListingTitle" />
  );

  // Helper function to create initial values for tour schedules
  const getTourSchedulesInitialValues = () => {
    const tourSchedules = publicData?.tourSchedules || [];

    // If no tour schedules exist, return empty array (users can add them optionally)
    if (tourSchedules.length === 0) {
      return [];
    }

    // Convert stored tour schedules to form format
    return tourSchedules.map(schedule => ({
      location:
        schedule.location?.address && schedule.location?.coordinates
          ? {
              search: schedule.location.address,
              selectedPlace: {
                address: schedule.location.address,
                origin: new LatLng(
                  schedule.location.coordinates.lat,
                  schedule.location.coordinates.lng
                ),
              },
            }
          : null,
      startDate: schedule.startDate ? { date: new Date(schedule.startDate) } : null,
      endDate: schedule.endDate ? { date: new Date(schedule.endDate) } : null,
    }));
  };

  const form = (
    <EditListingTouringLinksForm
      className={css.form}
      initialValues={{
        price: price ? new Money(price.amount, price.currency) : null,
        currency: publicData.currency || config.currency,
        touringLink: publicData?.touringLink,
        availabilityStatus: publicData?.availabilityStatus || 'available',
        tourSchedules: getTourSchedulesInitialValues(),
      }}
      onSubmit={values => {
        const { price, currency, touringLink, availabilityStatus, tourSchedules = [] } = values;

        // Process tour schedules data
        const processedTourSchedules = (tourSchedules || [])
          .filter(schedule => {
            // Filter out empty schedules (all fields null/empty)
            return (
              schedule.location?.selectedPlace?.address ||
              schedule.startDate?.date ||
              schedule.endDate?.date
            );
          })
          .map(schedule => ({
            location: {
              address: schedule.location?.selectedPlace?.address || '',
              coordinates: schedule.location?.selectedPlace?.origin
                ? {
                    lat: schedule.location.selectedPlace.origin.lat,
                    lng: schedule.location.selectedPlace.origin.lng,
                  }
                : null,
            },
            startDate: schedule.startDate?.date ? schedule.startDate.date.valueOf() : null,
            endDate: schedule.endDate?.date ? schedule.endDate.date.valueOf() : null,
          }));

        const updatedValues = {
          price: { amount: price.amount, currency: currency || price.currency },
          publicData: {
            currency,
            touringLink,
            availabilityStatus,
            tourSchedules: processedTourSchedules,
          },
        };

        onSubmit(updatedValues);
      }}
      onChange={onChange}
      saveActionMsg={submitButtonText}
      disabled={disabled}
      ready={ready}
      updated={panelUpdated}
      updateInProgress={updateInProgress}
      fetchErrors={errors}
      listing={currentListing}
      isPublished={isPublished}
      panelTitle={panelTitle}
      onGetPrevPathParams={onGetPrevPathParams}
    />
  );

  return <div className={classes}>{form}</div>;
};

const { func, object, string, bool } = PropTypes;

EditListingTouringLinksPanel.defaultProps = {
  className: null,
  rootClassName: null,
  listing: null,
};

EditListingTouringLinksPanel.propTypes = {
  className: string,
  rootClassName: string,

  // We cannot use propTypes.listing since the listing might be a draft.
  listing: object,

  disabled: bool.isRequired,
  ready: bool.isRequired,
  onSubmit: func.isRequired,
  onChange: func.isRequired,
  submitButtonText: string.isRequired,
  panelUpdated: bool.isRequired,
  updateInProgress: bool.isRequired,
  errors: object.isRequired,
};

export default EditListingTouringLinksPanel;
